import { useEffect, useRef } from 'react';

const GlassEffect = ({
  darkMode = false,
  panelCount = 6,
  panelWidth = 220,
  opacity = 0.44,
  speed = 2.2,
  gap = 23,
  blur = 12,
  // Новые параметры для контроля цвета
  bgColor = null, // если null, используем автоматический цвет
  borderColor = null, // если null, используем автоматический цвет
  borderWidth = '1px',
  borderRadius = '12px',
  // Новые параметры для контроля теней
  shadowColor = null,
  shadowBlur = 32,
  shadowOffsetY = 8,
  shadowOpacity = null, // если null, используем автоматический
  // Дополнительные параметры
  customClassName = '',
  customStyles = {},
}) => {
  const containerRef = useRef(null);
  const timeRef = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let animationFrameId;
    const cycleWidth = panelCount * (panelWidth + gap);
    const panels = [];

    for (let i = 0; i < panelCount; i++) {
      panels.push({
        x: i * (panelWidth + gap) - cycleWidth * 0.4,
        speed: speed * (0.9 + Math.random() * 0.2),
        phase: Math.random() * Math.PI * 2,
      });
    }

    const animate = () => {
      timeRef.current++;
      const panelElements = container.children;
      const windowWidth = window.innerWidth;

      panels.forEach((panel, i) => {
        panel.x += panel.speed;

        if (panel.x > windowWidth + panelWidth) {
          panel.x -= cycleWidth + panelWidth * 1.5;
        }

        const pulse = 0.88 + Math.sin(timeRef.current * 0.015 + panel.phase) * 0.12;

        const el = panelElements[i];
        if (el) {
          el.style.transform = `translate3d(${panel.x}px, 0, 0)`;
          el.style.opacity = pulse;
        }
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => cancelAnimationFrame(animationFrameId);
  }, [panelCount, panelWidth, opacity, speed, gap, darkMode]);

  // Функция для генерации стилей
  const getPanelStyles = () => {
    // Цвет фона
    let backgroundColor;
    if (bgColor) {
      backgroundColor = bgColor;
    } else {
      backgroundColor = darkMode
        ? `rgba(255, 255, 255, ${opacity * 0.18})`
        : `rgba(0, 0, 0, ${opacity * 0.06})`;
    }

    // Цвет border
    let borderColorValue;
    if (borderColor) {
      borderColorValue = borderColor;
    } else {
      borderColorValue = darkMode
        ? `rgba(255, 255, 255, ${opacity * 0.22})`
        : `rgba(0, 0, 0, ${opacity * 0.12})`;
    }

    // Тень
    let boxShadow;
    if (shadowColor) {
      const shadowOpacityValue = shadowOpacity !== null ? shadowOpacity : 0.3;
      boxShadow = `0 ${shadowOffsetY}px ${shadowBlur}px ${shadowColor}`;
    } else {
      boxShadow = darkMode
        ? `0 ${shadowOffsetY}px ${shadowBlur}px rgba(0, 0, 0, ${shadowOpacity !== null ? shadowOpacity : 0.35})`
        : `0 ${shadowOffsetY}px ${shadowBlur}px rgba(0, 0, 0, ${shadowOpacity !== null ? shadowOpacity : 0.06})`;
    }

    return {
      width: panelWidth,
      backdropFilter: `blur(${blur}px)`,
      WebkitBackdropFilter: `blur(${blur}px)`,
      borderRadius: borderRadius,
      borderStyle: 'solid',
      borderColor: borderColorValue,
      borderTopWidth: 0,    
      borderBottomWidth: 0,       
      borderLeftWidth: borderWidth,   
      borderRightWidth: borderWidth,  
      backgroundColor: backgroundColor,
      boxShadow: boxShadow,
      willChange: 'transform, opacity',
      ...customStyles,
    };
  };

  return (
    <div
      ref={containerRef}
      className={`fixed inset-0 w-full h-full pointer-events-none overflow-hidden z-0 ${customClassName}`}
    >
      {Array.from({ length: panelCount }).map((_, i) => (
        <div
          key={i}
          className="absolute top-0 h-full"
          style={getPanelStyles()}
        />
      ))}
    </div>
  );
};

export default GlassEffect;